import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@datanomik/commons-ui";
import { CloseLine, SendMoney } from "@datanomik/commons-ui/icons";
import { CheckCircleOutline, ShieldOutlined, RefreshOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import AnimatedEntranceAndExit from "@/views/common/AnimatedEntranceAndExit";
import { useDirectTransferForm, type TransferStep } from "./useDirectTransferForm";
import { DirectTransferDetailsStep } from "./steps/DirectTransferDetailsStep";
import { DirectTransferConfirmStep, formatCurrency } from "./steps/DirectTransferConfirmStep";
import type { AccountResponse } from "../types/account.types";
import dayjs from "dayjs";

const TRANSFER_MODE_LABELS: Record<string, string> = {
  PIX: "PIX",
  BANK_TRANSFER: "TED",
};

// ─── Shared UI pieces ────────────────────────────────────────────────────────

const DotStepper: React.FC<{ current: number; total?: number }> = ({ current, total = 3 }) => (
  <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
    {Array.from({ length: total }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: i <= current ? "primary.main" : "grey.200",
        }}
      />
    ))}
  </Stack>
);

const BarStepper: React.FC<{ step: number }> = ({ step }) => (
  <Stack direction="row" spacing={0.625} alignItems="center" sx={{ flexShrink: 0 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: i === step ? 18 : 7,
          height: 4,
          borderRadius: 2,
          bgcolor: i <= step ? "primary.main" : "grey.200",
          transition: "all 0.4s",
        }}
      />
    ))}
  </Stack>
);

const DetailRows: React.FC<{ rows: { label: string; value: string; mono?: boolean }[] }> = ({ rows }) => (
  <Stack
    sx={{
      borderRadius: "8px",
      border: "1px solid",
      borderColor: "grey.200",
      overflow: "hidden",
    }}
  >
    {rows.map((r, i) => (
      <Stack
        key={i}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: i < rows.length - 1 ? "1px solid" : "none",
          borderColor: "grey.100",
        }}
      >
        <Typography fontSize={12} color="text.secondary">
          {r.label}
        </Typography>
        <Typography
          fontSize={12.5}
          fontWeight={500}
          sx={{ fontFamily: r.mono ? "monospace" : undefined }}
        >
          {r.value}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

const SecurityNote: React.FC<{ label: string }> = ({ label }) => (
  <Stack direction="row" alignItems="center" justifyContent="center" gap={0.75} sx={{ mt: 1.5 }}>
    <ShieldOutlined sx={{ fontSize: 14, color: "text.disabled" }} />
    <Typography fontSize={11.5} color="text.disabled">
      {label}
    </Typography>
  </Stack>
);

// ─── Processing view ─────────────────────────────────────────────────────────

const ProcessingView: React.FC<{ amount: string }> = ({ amount }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);

  const processingSteps = [
    { label: t("Validating"), threshold: 25 },
    { label: t("Authorizing"), threshold: 55 },
    { label: t("Sending"), threshold: 85 },
  ];

  useEffect(() => {
    const steps = [
      { t: 300, p: 25 },
      { t: 800, p: 55 },
      { t: 1300, p: 85 },
      { t: 1800, p: 100 },
    ];
    const timers = steps.map((s) => setTimeout(() => setProgress(s.p), s.t));
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <Stack>
      {/* Progress bar */}
      <Box sx={{ height: 3, bgcolor: "grey.100" }}>
        <Box
          sx={{
            height: "100%",
            bgcolor: "primary.main",
            borderRadius: 1,
            width: `${progress}%`,
            transition: "width 0.5s ease",
          }}
        />
      </Box>

      <Stack alignItems="center" sx={{ px: 3, py: 7.5, gap: 2.5 }}>
        <CircularProgress size={48} thickness={3} />

        <Typography fontSize={28} fontWeight={700} letterSpacing="-0.02em">
          {formatCurrency(amount)}
        </Typography>

        <Typography fontSize={13} color="text.disabled">
          {t("Processing transfer...")}
        </Typography>

        <Stack direction="row" gap={3} sx={{ mt: 1 }}>
          {processingSteps.map((s, i) => {
            const done = progress >= s.threshold;
            return (
              <Stack key={i} direction="row" alignItems="center" gap={0.625}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: done ? "success.main" : "grey.100",
                    border: "1.5px solid",
                    borderColor: done ? "success.light" : "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                  }}
                >
                  {done && <CheckCircleOutline sx={{ fontSize: 10, color: "white" }} />}
                </Box>
                <Typography
                  fontSize={11}
                  fontWeight={600}
                  sx={{
                    color: done ? "success.main" : "text.disabled",
                    transition: "color 0.3s",
                  }}
                >
                  {s.label}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
};

// ─── Success view ────────────────────────────────────────────────────────────

const TransferSuccessView: React.FC<{
  destinationAccount: AccountResponse | null;
  amount: string;
  transferMode: string;
  description: string;
  onNewTransfer: () => void;
  onClose: () => void;
}> = ({ destinationAccount, amount, transferMode, description, onNewTransfer, onClose }) => {
  const { t } = useTranslation();
  const ts = dayjs().format("DD/MM/YYYY [às] HH:mm");

  return (
    <Stack>
      <Stack sx={{ px: 3, py: 1, gap: 2 }}>
        <Stack alignItems="center" sx={{ pt: 2.5, pb: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "success.lighter",
              border: "2px solid",
              borderColor: "success.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <Typography fontSize={26} sx={{ lineHeight: 1, color: "success.main" }}>✓</Typography>
          </Box>
          <Typography fontSize={36} fontWeight={700} letterSpacing="-0.02em" sx={{ lineHeight: 1 }}>
            {formatCurrency(amount)}
          </Typography>
          <Typography fontSize={11} fontWeight={500} color="success.main" sx={{ mt: 0.75, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("Transfer completed")}
          </Typography>
        </Stack>

        <Divider />

        <DetailRows
          rows={[
            { label: t("Sent to"), value: destinationAccount?.owner.fiscalName ?? "-" },
            { label: t("Destination account"), value: destinationAccount ? `Ag. ${destinationAccount.account.branch} · CC: ${destinationAccount.account.accountNumber}` : "-", mono: true },
            { label: t("Mode"), value: TRANSFER_MODE_LABELS[transferMode] ?? transferMode },
            { label: t("Date & time"), value: ts },
            ...(description ? [{ label: t("Description"), value: description }] : []),
          ]}
        />

        <SecurityNote label={t("Protected and audited transfer")} />
      </Stack>

      <Stack
        direction="row"
        spacing={1.25}
        sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.100", flexShrink: 0 }}
      >
        <Button variant="outlined" color="black" fullWidth onClick={onClose}>{t("Close")}</Button>
        <Button variant="contained" fullWidth onClick={onNewTransfer}>{t("New transfer")}</Button>
      </Stack>
    </Stack>
  );
};

// ─── Error view ──────────────────────────────────────────────────────────────

const TransferErrorView: React.FC<{
  amount: string;
  errorMessage?: string | null;
  onTryAgain: () => void;
  onClose: () => void;
}> = ({ amount, errorMessage, onTryAgain, onClose }) => {
  const { t } = useTranslation();

  return (
    <Stack>
      <Stack sx={{ px: 3, py: 1, gap: 2 }}>
        <Stack alignItems="center" sx={{ pt: 2.5, pb: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "error.lighter",
              border: "2px solid",
              borderColor: "error.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <Typography fontSize={26} sx={{ lineHeight: 1, color: "error.main" }}>✕</Typography>
          </Box>
          <Typography fontSize={36} fontWeight={700} letterSpacing="-0.02em" sx={{ lineHeight: 1, opacity: 0.4 }}>
            {formatCurrency(amount)}
          </Typography>
          <Typography fontSize={11} fontWeight={500} color="error.main" sx={{ mt: 0.75, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("Transfer failed")}
          </Typography>
        </Stack>

        <Divider />

        <Stack
          sx={{
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "error.light",
            bgcolor: "error.lighter",
            px: 2,
            py: 1.75,
          }}
        >
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "error.main" }} />
            <Typography fontSize={12} fontWeight={700} color="error.main">
              {t("Transaction error")}
            </Typography>
          </Stack>
          <Typography fontSize={12.5} color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {errorMessage || t("An error occurred while processing the transfer. Check the data and available balance in the origin account.")}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={1.25}
        sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.100", flexShrink: 0 }}
      >
        <Button variant="outlined" color="black" fullWidth onClick={onClose}>{t("Close")}</Button>
        <Button
          variant="contained"
          fullWidth
          onClick={onTryAgain}
          startIcon={<RefreshOutlined sx={{ fontSize: 16 }} />}
        >
          {t("Try again")}
        </Button>
      </Stack>
    </Stack>
  );
};

// ─── Dialog ──────────────────────────────────────────────────────────────────

interface DirectTransferDialogProps {
  open: boolean;
  onClose: () => void;
  fiscalNumber?: string;
}

export const DirectTransferDialog: React.FC<DirectTransferDialogProps> = ({ open, onClose, fiscalNumber }) => {
  const { t } = useTranslation();

  const {
    form,
    step,
    onContinue,
    onConfirm,
    onBackToDetails,
    onNewTransfer,
    onTryAgain,
    isSubmitting,
    handleOriginAccountChange,
    handleDestinationAccountChange,
    originFiscalNumber,
    originAccountData,
    destinationAccountData,
    transferModes,
    errorMessage,
  } = useDirectTransferForm(fiscalNumber);

  const innerRef = useRef<HTMLDivElement>(null);
  const [dialogHeight, setDialogHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setDialogHeight(el.scrollHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const amountValue = form.watch("amount");
  const descriptionValue = form.watch("description");
  const transferModeValue = form.watch("transferMode");

  const handleClose = () => {
    onNewTransfer();
    onClose();
  };

  const isTerminal = step === "result" || step === "error";
  const isProcessing = step === "processing";

  const headerTitle = () => {
    if (step === "processing") return t("Processing transfer");
    if (step === "result") return t("Transfer completed");
    if (step === "error") return t("Transfer failed");
    if (step === "confirm") return t("Confirm transfer");
    return t("Transfer");
  };

  const headerSubtitle = () => {
    if (step === "processing") return t("Please wait...");
    if (step === "details") return t("Fill in the details");
    if (step === "confirm") return t("Review the data before confirming");
    if (step === "result") return t("Successfully processed");
    if (step === "error") return t("Could not process");
    return "";
  };

  const stepIndex = step === "details" ? 0 : step === "confirm" ? 1 : 2;
  const useDots = step === "details";

  return (
    <Dialog
      open={open}
      onClose={isTerminal || isProcessing ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "575px",
          height: dialogHeight ? `${dialogHeight}px` : "auto",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: "14px",
          overflow: "hidden",
          transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
    >
      <div ref={innerRef}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "grey.100" }}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            {useDots && (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: "primary.lighter",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <SendMoney sx={{ fontSize: 20, color: "primary.main" }} />
              </Box>
            )}
            <Stack>
              <Typography fontSize={16} fontWeight={700}>{headerTitle()}</Typography>
              <Typography fontSize={12.5} color="text.disabled" sx={{ mt: 0.25 }}>{headerSubtitle()}</Typography>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1.75}>
            {useDots ? <DotStepper current={stepIndex} /> : <BarStepper step={stepIndex} />}
            {!isProcessing && (
              <IconButton size="small" onClick={handleClose} sx={{ color: "text.disabled" }}>
                <CloseLine />
              </IconButton>
            )}
          </Stack>
        </Stack>

        {/* Step content */}
        <AnimatedEntranceAndExit
          key={step}
          isVisible
          direction="up"
          distance={14}
          duration={0.22}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {step === "details" && (
            <DirectTransferDetailsStep
              form={form}
              onContinue={onContinue}
              handleOriginAccountChange={handleOriginAccountChange}
              handleDestinationAccountChange={handleDestinationAccountChange}
              originFiscalNumber={originFiscalNumber}
              transferModes={transferModes}
              originAccountData={originAccountData}
              destinationAccountData={destinationAccountData}
            />
          )}
          {step === "confirm" && (
            <DirectTransferConfirmStep
              originAccount={originAccountData}
              destinationAccount={destinationAccountData}
              amount={amountValue}
              description={descriptionValue}
              transferMode={transferModeValue}
              isSubmitting={isSubmitting}
              onConfirm={onConfirm}
              onBack={onBackToDetails}
            />
          )}
          {step === "processing" && (
            <ProcessingView amount={amountValue} />
          )}
          {step === "result" && (
            <TransferSuccessView
              destinationAccount={destinationAccountData}
              amount={amountValue}
              transferMode={transferModeValue}
              description={descriptionValue}
              onNewTransfer={onNewTransfer}
              onClose={handleClose}
            />
          )}
          {step === "error" && (
            <TransferErrorView
              amount={amountValue}
              errorMessage={errorMessage}
              onTryAgain={onTryAgain}
              onClose={handleClose}
            />
          )}
        </AnimatedEntranceAndExit>
      </div>
    </Dialog>
  );
};
